import { boundMethod } from 'autobind-decorator';
import type { AxiosError } from 'axios';
import { SaveStatus } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import memoize from 'memoize-one';
import { PureComponent } from 'react';

import '../styles/chat-flow.less';
import '../styles/conversational-widget-modals.less';

import {
  type FlowExtraData,
  FlowExtraDataFetch,
} from 'components/powerFlowShared/components/extraDataFetch';
import {
  OpeningHoursNode,
  TagsNode,
} from 'components/powerFlowShared/components/nodeTypes';
import { CloseNodeChatAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/close';
import { ContactFormNodeChatAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/contactForm';
import { InitNodeChatAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/init';
import { OpeningHoursNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/openingHours';
import { QueueNodeChatAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/queue';
import { TagsNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/tags';
import { actionDiff } from 'components/powerFlowShared/helpers/actionDiff';
import { getActionNodes } from 'components/powerFlowShared/helpers/getInvalidNodes';
import { FlowEditorChrome, FlowType } from 'fable/flowEditor';
import { RouteComponentProps } from 'hocs/router';

import type { FlowChatActionT, FlowChatCreateFlowT } from '../api/types';
import type {
  FlowChatDispatchPropsT,
  FlowChatStatePropsT,
} from '../containers/powerChatFlow';

import {
  ChatinputNode,
  ChatmenuNode,
  ChatmessageNode,
  ContactFormNode,
  HideStrategyNode,
  HideWidgetNode,
  InitNode,
  QueueNode,
  URLTriggerNode,
  WidgetCustomizationNode,
} from './nodeTypes';
import { ChatInputNodeAttributes } from './nodeTypes/canvas/chatInput';
import { ChatMenuNodeAttributes } from './nodeTypes/canvas/chatMenu';
import { ChatMessageNodeAttributes } from './nodeTypes/canvas/chatMessage';
import { HideStrategyNodeAttributes } from './nodeTypes/canvas/hideStrategy';
import { URLTriggerNodeAttributes } from './nodeTypes/canvas/url';
import { WidgetCustomizationNodeAttributes } from './nodeTypes/canvas/widgetCustomization';

export type PowerChatFlowExtraDataT = {
  name: string;
  updated_by: string | null;
} & FlowExtraData;

export type FlowChatOwnPropsT = RouteComponentProps<'flowId'> & {
  location: {
    state: null | string;
  };
};

type FlowChatPropsT = FlowChatOwnPropsT &
  FlowChatStatePropsT &
  FlowChatDispatchPropsT;

type FlowChatStateT = {
  actions: FlowChatActionT[] | null;
  active: boolean;
  invalidNodes: null | string[];
  name: string;
  nodeTypes: {};
  number: null | number;
  ready: boolean;
  saveStatus: SaveStatus;
};

class PowerChatFlow extends PureComponent<FlowChatPropsT, FlowChatStateT> {
  static getDerivedStateFromProps(
    props: FlowChatPropsT,
    state: FlowChatStateT,
  ) {
    if (
      props.status === 'succeeded' &&
      !state.ready &&
      props.flowData.name &&
      props.flowData.actions.length &&
      props.flowData.id === props.id
    ) {
      // flow is ready, and hasn't been initialized yet..
      // we update flow info in state
      return {
        saveStatus: SaveStatus.Saved,
        ready: true,
        name: props.flowData.name,
        actions: props.flowData.actions,
        active: true,
      };
    }
    if (props.status === 'failed') {
      return { saveStatus: SaveStatus.Failed };
    }
    if (
      props.status === 'succeeded' &&
      (state.saveStatus === SaveStatus.Saving ||
        state.saveStatus === SaveStatus.Failed)
    ) {
      return { saveStatus: SaveStatus.Saved };
    }

    // no change
    return null;
  }

  constructor(props: FlowChatPropsT) {
    super(props);

    const { isCanvasEnabled } = props;

    this.state = {
      actions: null,
      active: true,
      invalidNodes: null,
      name: '',
      nodeTypes: isCanvasEnabled
        ? {
            init: InitNodeChatAttributes,
            // GREEN - Termination -> Queue
            queue: QueueNodeChatAttributes,
            contactform: ContactFormNodeChatAttributes,
            // RED - Termination -> Closed
            hidden: CloseNodeChatAttributes,
            // PURPLE - Splitters
            hidestrategy: HideStrategyNodeAttributes,
            chatmenu: ChatMenuNodeAttributes,
            openinghours: OpeningHoursNodeAttributes,
            url: URLTriggerNodeAttributes,
            // ORANGE - Input required
            // BLUE - Other
            chatinput: ChatInputNodeAttributes,
            chatmessage: ChatMessageNodeAttributes,
            widgetcustomization: WidgetCustomizationNodeAttributes,
            ...(props.hasTagNode && { tags: TagsNodeAttributes }),
          }
        : {
            init: InitNode,
            // GREEN - Termination -> Queue
            queue: QueueNode,
            contactform: ContactFormNode,
            // RED - Termination -> Closed
            hidden: HideWidgetNode,
            // PURPLE - Splitters
            hidestrategy: HideStrategyNode,
            chatmenu: ChatmenuNode,
            openinghours: OpeningHoursNode,
            url: URLTriggerNode,
            // ORANGE - Input required
            // BLUE - Other
            chatinput: ChatinputNode,
            chatmessage: ChatmessageNode,
            widgetcustomization: WidgetCustomizationNode,
            ...(props.hasTagNode && { tags: TagsNode }),
          },
      number: null,
      ready: false,
      saveStatus: SaveStatus.Saved,
    };
  }

  componentDidMount() {
    if (this.props.id) {
      this.props.fetchFlow({ id: this.props.id });
    }
  }

  componentDidUpdate(pProps: FlowChatPropsT) {
    const { getWidget, widget_id: widgetId } = this.props;

    const { widget_id: prevWidgetId } = pProps;

    if (!prevWidgetId && widgetId) {
      getWidget(widgetId);
    }
  }

  componentWillUnmount() {
    // restore Chat Flow initial state
    this.props.chatFlowClearState();
  }

  @boundMethod
  hasUnsavedChanges() {
    return this.state.saveStatus <= 0;
  }

  @boundMethod
  handleOnChange({
    name,
    actions,
    active,
  }: {
    name: string;
    actions: FlowChatActionT[];
    active: boolean;
  }): void {
    if (
      !this.hasUnsavedChanges() ||
      this.state.name !== name ||
      this.state.actions !== actions ||
      this.state.active !== active
    ) {
      this.setState({
        name,
        actions,
        active,
        saveStatus: SaveStatus.Unsaved,
        invalidNodes: null,
      });
    }
  }

  @boundMethod
  getFullFlow(local = false) {
    const { name, actions } = this.state;
    const { id, widget_id, agentDisplayName } = this.props;

    return this.getFullFlowMemo(
      local,
      id || 'local',
      name,
      actions,
      agentDisplayName,
      widget_id,
    );
  }

  getFullFlowMemo = memoize(
    (_local, id, name, actions, agentDisplayName, widgetId?) => {
      return {
        id,
        name: name || 'Name your flow',
        actions,
        active: true,
        location: widgetId,
        updated_by: agentDisplayName || null,
        channel: 'widgetchat' as const,
      };
    },
  );

  @boundMethod
  handleOnSave() {
    const { actions } = this.state;
    const invalidNodes = actions && getActionNodes(actions);

    if (invalidNodes && invalidNodes.length) {
      this.setState({
        invalidNodes,
      });
    } else {
      this.setState(
        {
          saveStatus: SaveStatus.Saving,
          invalidNodes: null,
        },
        this.saveOrUpdateFlow,
      );
    }
  }

  @boundMethod
  saveOrUpdateFlow() {
    const fullFlow = this.getFullFlow();
    if (fullFlow.id === 'local' || fullFlow.id === null) {
      this.saveFlow(fullFlow);
    } else {
      this.updateFlow(fullFlow);
    }
  }

  @boundMethod
  saveFlow(fullFlow: FlowChatCreateFlowT) {
    this.props
      .addFlow({ flow: fullFlow })
      .then(() => {
        const notificationOptions = {
          title: 'New chat flow saved',
          text: 'Saved',
          icon: 'i-done',
          className: 'success',
          time: 5000,
          showDesktop: false,
        };
        this.props.notificationAdd(notificationOptions);
      })
      .catch((err: Error) => {
        console.error(err);
        this.setState({
          saveStatus: SaveStatus.Failed,
        });
      });
  }

  @boundMethod
  updateFlow(fullFlow: FlowChatCreateFlowT) {
    if (this.props.id && this.props.widget_id && this.state.name) {
      if (
        this.state.name.trim().length <= 2 ||
        this.state.name.trim().length > 255
      ) {
        this.props.validateWidgetName(this.state.name);
        return;
      }
      this.props.updateWidget({
        id: this.props.widget_id,
        name: this.state.name,
      });

      this.props
        .updateFlow({ id: this.props.id, flow: fullFlow })
        .then(() => {
          const notificationOptions = {
            title: 'Saved',
            text: `"${this.state.name}" is saved.`,
            icon: 'i-done',
            className: 'success',
            time: 3000,
            showDesktop: false,
          };
          this.props.notificationAdd(notificationOptions);
        })
        .catch((err: AxiosError<any>) => {
          console.error(err);
          const { flowData } = this.props;
          Sentry.captureException(err, {
            contexts: {
              response: err?.response?.data?.data,
              flow: {
                data: fullFlow,
                diff: actionDiff(fullFlow.actions, flowData.actions),
              },
            },
            tags: { team: SentryTeam.Routing },
          });
          const notificationOptions = {
            title: 'Could not save',
            text: `There was an issue when "${
              this.state.name
            }" was saved. ${err.toString()}`,
            icon: 'i-exclamation',
            className: 'warning',
            showDesktop: false,
          };
          this.props.notificationAdd(notificationOptions);
          this.setState({
            saveStatus: SaveStatus.Failed,
          });
        });
    }
  }

  @boundMethod
  handleOnActivate() {
    this.setState(
      {
        active: true,
      },
      this.saveOrUpdateFlow,
    );
  }

  render() {
    const { flowData, agentDisplayName } = this.props;
    const { ready, actions, invalidNodes, name, nodeTypes } = this.state;

    return (
      <FlowExtraDataFetch
        actions={actions}
        render={(extraData) => (
          <div className="powercallflow-container">
            {ready && (
              <FlowEditorChrome
                errorMessage={null}
                extraData={{
                  name,
                  updated_by: agentDisplayName || null,
                  ...extraData,
                }}
                flowType={FlowType.FLOW_TYPE_CHAT}
                goBackPath="/settings/chat"
                hasUnsavedChanges={this.hasUnsavedChanges()}
                initialData={flowData}
                invalidNodes={invalidNodes}
                nodeTypes={nodeTypes}
                onActivate={this.handleOnActivate}
                onChange={this.handleOnChange}
                onSave={this.handleOnSave}
              />
            )}
          </div>
        )}
      />
    );
  }
}

export default PowerChatFlow;
